import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SparePartsService } from '../../../services/spare-parts.service';
import { SparePart, SparePartsFilters } from '../../../models/mrsearch.models';

@Component({
  selector: 'app-spc',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './spc.component.html',
  styleUrl: './spc.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SpcComponent implements OnInit {
  title = 'Spare Parts Catalog';
  
  // Search properties  
  searchQuery: string = '';
  bookCode: string = '';
  pageReference: string = '';
  partNumber: string = '';
  
  // Results
  spareParts: SparePart[] = [];
  filteredSpareParts: SparePart[] = [];
  isLoading: boolean = false;
  hasSearched: boolean = false;
  errorMessage: string = '';
  
  // Pagination
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalResults: number = 0;

  constructor(private sparePartsService: SparePartsService) {}

  ngOnInit(): void {
    // Initialize component
  }

  /**
   * Perform search with current query and filters
   */
  public onSearch(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.hasSearched = true;
    this.currentPage = 1;

    const filters: SparePartsFilters = {};
    if (this.bookCode) filters.book_code = this.bookCode;
    if (this.pageReference) filters.page_reference = this.pageReference;
    if (this.partNumber) filters.part_number = this.partNumber;

    // Debug logging
    console.log('Search initiated with:', {
      query: this.searchQuery,
      filters: filters,
      top: 100
    });

    this.sparePartsService.searchSpareParts({
      query: this.searchQuery,
      filters: Object.keys(filters).length > 0 ? filters : undefined,
      top: 100 // Fetch more results 
    }).subscribe({
      next: (response) => {
        console.log('Search response:', response);
        this.spareParts = response.results;
        this.filteredSpareParts = this.spareParts;
        this.totalResults = response.total_count;
        
        // Load images for parts that have image files
        this.loadImages();
        
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Search error:', error);
        this.errorMessage = 'Failed to search spare parts. Please try again.';
        this.spareParts = [];
        this.filteredSpareParts = [];
        this.totalResults = 0;
        this.isLoading = false;
      }
    });
  }

  /**
   * Load image SAS URLs for parts with images
   */
  private loadImages(): void {
    this.sparePartsService.loadImagesForParts(this.spareParts).subscribe({
      next: (parts) => {
        this.spareParts = parts;
        this.filteredSpareParts = [...this.spareParts];
      },
      error: (error) => {
        console.error('Error loading images:', error);
      }
    });
  }

  /**
   * Clear all filters and search
   */
  public clearFilters(): void {
    this.searchQuery = '';
    this.bookCode = '';
    this.pageReference = '';
    this.partNumber = '';
    this.spareParts = [];
    this.filteredSpareParts = [];
    this.hasSearched = false;
    this.errorMessage = '';
    this.currentPage = 1;
  }

  /**
   * Get paginated results
   */
  public get paginatedResults(): SparePart[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.filteredSpareParts.slice(startIndex, endIndex);
  }

  /**
   * Get the range of items being displayed (e.g., "1-10 of 50")
   */
  public get displayRange(): string {
    if (this.filteredSpareParts.length === 0) {
      return '0';
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage + 1;
    const endIndex = Math.min(this.currentPage * this.itemsPerPage, this.filteredSpareParts.length);
    return `${startIndex}-${endIndex}`;
  }

  /**
   * Get total number of pages
   */
  public get totalPages(): number {
    if (this.filteredSpareParts.length === 0) {
      return 1;
    }
    return Math.ceil(this.filteredSpareParts.length / this.itemsPerPage);
  }

  /**
   * Go to next page
   */
  public nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /**
   * Go to previous page
   */
  public previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /**
   * Go to specific page
   */
  public goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /**
   * Check if part has an image available
   */
  public hasImage(part: SparePart): boolean {
    return !!part.image_file && !!part.image_sas_url;
  }

  /**
   * Handle image loading errors
   */
  public onImageError(part: SparePart): void {
    part.image_sas_url = undefined;
    part.imageLoading = false;
  }
}

